// Brand Session Authority session client (ADR-0116).
export interface AppLink {
  path: string
  href: string
  label: string
}

export interface CompanyContext {
  partyRoleId: string
  partyUserId: string
  name: string
  selected: boolean
  appLinks: AppLink[]
}

export type SessionStatus =
  | { kind: 'signed-out' }
  | { kind: 'signed-in', email: string, displayName: string, contexts: CompanyContext[] }
  | { kind: 'error' }

export type ContextSelection =
  | { kind: 'redirect', redirectUrl: string }
  | { kind: 'signed-out' }
  | { kind: 'not-authorized' }
  | { kind: 'error' }

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function readAppLinks(value: unknown): AppLink[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value
    .map(raw => raw as Record<string, unknown>)
    .filter(raw => raw !== null && typeof raw === 'object' && text(raw.path) !== '')
    .map(raw => ({ path: text(raw.path), href: text(raw.href), label: text(raw.label) }))
}

function readContexts(value: unknown): CompanyContext[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value
    .map(raw => raw as Record<string, unknown>)
    .filter(raw => raw !== null && typeof raw === 'object' && text(raw.partyRoleId) !== '')
    .map(raw => ({
      partyRoleId: text(raw.partyRoleId),
      partyUserId: text(raw.partyUserId),
      name: text(raw.name),
      selected: raw.selected === true,
      appLinks: readAppLinks(raw.appLinks),
    }))
}

// A refused or unreachable status means no session.
export async function readSessionStatus(
  endpoint: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<SessionStatus> {
  let response: Response
  try {
    response = await fetchImpl(endpoint, {
      credentials: 'include',
      headers: { accept: 'application/json' },
    })
  }
  catch {
    return { kind: 'signed-out' }
  }

  if (!response.ok) {
    return { kind: 'signed-out' }
  }

  let payload: unknown
  try {
    payload = await response.json()
  }
  catch {
    return { kind: 'error' }
  }

  const body = payload as Record<string, unknown> | null
  if (body === null || typeof body !== 'object') {
    return { kind: 'error' }
  }
  if (body.isAuthenticated !== true) {
    return { kind: 'signed-out' }
  }

  return {
    kind: 'signed-in',
    email: text(body.email),
    displayName: text(body.displayName),
    contexts: readContexts(body.availableContexts),
  }
}

export interface ContextRequest {
  endpoint: string
  partyRoleId: string
  returnUrl: string | null
  app: string | null
  fetchImpl?: typeof fetch
}

// returnUrl wins: it is the broker's own destination.
export async function selectContext({
  endpoint,
  partyRoleId,
  returnUrl,
  app,
  fetchImpl = globalThis.fetch,
}: ContextRequest): Promise<ContextSelection> {
  const body: Record<string, string> = { partyRoleId }
  if (returnUrl !== null) {
    body.returnUrl = returnUrl
  }
  else if (app !== null) {
    body.app = app
  }

  try {
    const response = await fetchImpl(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers: { 'accept': 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })

    // A refusal is not a retry: it has its own screen.
    if (response.status === 401) {
      return { kind: 'signed-out' }
    }
    if (response.status === 403) {
      return { kind: 'not-authorized' }
    }

    const payload: unknown = await response.json()
    const redirectUrl = (payload as { redirectUrl?: unknown } | null)?.redirectUrl

    if (!response.ok || typeof redirectUrl !== 'string' || redirectUrl === '') {
      return { kind: 'error' }
    }

    return { kind: 'redirect', redirectUrl }
  }
  catch {
    return { kind: 'error' }
  }
}
